/**
 * Phase 32 Plan 32-2 — project ↔ repository mapping TanStack Query hooks.
 *
 * Query keys:
 *   ['project-repositories', projectId] — linked repos for a project
 *   ['project-repositories', 'available'] — full catalogue (all active GH repos)
 *
 * Mutations invalidate BOTH keys on success because linking/unlinking
 * changes which catalogue rows are still "available" to add.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAvailableRepositories,
  fetchProjectRepositories,
  linkRepository,
  unlinkRepository,
  type GithubRepository,
  type ProjectRepository,
} from '../services/api/projectRepositories';

export function useProjectRepositories(projectId: number | undefined) {
  return useQuery<ProjectRepository[]>({
    queryKey: ['project-repositories', projectId ?? null],
    queryFn: () => fetchProjectRepositories(projectId as number),
    enabled: projectId !== undefined && Number.isFinite(projectId) && (projectId as number) > 0,
  });
}

export function useAvailableRepositories(enabled = true) {
  return useQuery<GithubRepository[]>({
    queryKey: ['project-repositories', 'available'],
    queryFn: () => fetchAvailableRepositories(),
    enabled,
  });
}

export function useLinkRepository(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (githubRepositoryId: string) => linkRepository(projectId, githubRepositoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-repositories', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-repositories', 'available'] });
      queryClient.invalidateQueries({ queryKey: ['project-engineering-stats', projectId] });
    },
  });
}

export function useUnlinkRepository(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (githubRepositoryId: string) => unlinkRepository(projectId, githubRepositoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-repositories', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-repositories', 'available'] });
      queryClient.invalidateQueries({ queryKey: ['project-engineering-stats', projectId] });
    },
  });
}
